// Módulo Vuex para la configuración del producto por DOS EJES:
//   manejaPrecios (P) y pickingConOperarios (O).
// Pyme/Empresa son presets. Se persiste en localStorage entre sesiones.
import {
  EDITIONS,
  EDITION_PRESETS,
  DEFAULT_FLAGS,
  isModuleVisible,
  editionFromFlags,
  vocab
} from '@/config/editions'

const STORAGE_KEY = 'wms-edition-flags'
const LEGACY_KEY = 'wms-edition' // edición previa (solo Pyme/Empresa)

// Edición por defecto del build (VUE_APP_DEFAULT_EDITION=salud en .env.dm): en el
// spin-off de trazabilidad médica todo navegador nuevo parte como Institución de salud,
// sin pasar por las 3 preguntas del onboarding (los interruptores del menú siguen disponibles).
const DEFAULT_EDITION = process.env.VUE_APP_DEFAULT_EDITION

function loadState() {
  // 1) Formato nuevo (flags)
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (DEFAULT_EDITION && EDITION_PRESETS[DEFAULT_EDITION] && !parsed.onboardingCompleto) {
        return { ...EDITION_PRESETS[DEFAULT_EDITION], onboardingCompleto: true }
      }
      return {
        manejaPrecios: !!parsed.manejaPrecios,
        pickingConOperarios: !!parsed.pickingConOperarios,
        sectorSalud: !!parsed.sectorSalud,
        onboardingCompleto: !!parsed.onboardingCompleto
      }
    }
  } catch (e) { /* ignorar */ }

  // 2) Migración desde el formato viejo (solo edición Pyme/Empresa)
  const legacy = localStorage.getItem(LEGACY_KEY)
  if (legacy && EDITION_PRESETS[legacy]) {
    return { ...EDITION_PRESETS[legacy], onboardingCompleto: true }
  }

  // 3) Edición por defecto del build (salud) o default con onboarding pendiente
  if (DEFAULT_EDITION && EDITION_PRESETS[DEFAULT_EDITION]) {
    return { ...EDITION_PRESETS[DEFAULT_EDITION], onboardingCompleto: true }
  }
  return { ...DEFAULT_FLAGS, onboardingCompleto: false }
}

function persist(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    manejaPrecios: state.manejaPrecios,
    pickingConOperarios: state.pickingConOperarios,
    sectorSalud: state.sectorSalud,
    onboardingCompleto: state.onboardingCompleto
  }))
}

export default {
  namespaced: true,
  state: loadState(),
  getters: {
    manejaPrecios: state => state.manejaPrecios,
    pickingConOperarios: state => state.pickingConOperarios,
    sectorSalud: state => state.sectorSalud,
    // Vocabulario según sector (p. ej. vocab('egresos') → 'Salidas a servicios')
    vocab: state => key => vocab(key, state),
    onboardingCompleto: state => state.onboardingCompleto,
    // Etiqueta de edición derivada de los dos ejes (compatibilidad)
    edition: state => editionFromFlags(state),
    isEmpresa: state => state.manejaPrecios && state.pickingConOperarios,
    // Función para evaluar visibilidad de un módulo según los dos ejes
    isModuleVisible: state => moduleKey => isModuleVisible(moduleKey, state)
  },
  mutations: {
    setFlags(state, { manejaPrecios, pickingConOperarios, sectorSalud }) {
      if (typeof manejaPrecios === 'boolean') state.manejaPrecios = manejaPrecios
      if (typeof pickingConOperarios === 'boolean') state.pickingConOperarios = pickingConOperarios
      if (typeof sectorSalud === 'boolean') state.sectorSalud = sectorSalud
      persist(state)
    },
    setFlag(state, { key, value }) {
      if (key === 'manejaPrecios' || key === 'pickingConOperarios' || key === 'sectorSalud') {
        state[key] = !!value
        persist(state)
      }
    },
    // Compat: aplicar un preset por edición (Pyme/Empresa)
    setEdition(state, edition) {
      const preset = EDITION_PRESETS[edition]
      if (!preset) return
      state.manejaPrecios = preset.manejaPrecios
      state.pickingConOperarios = preset.pickingConOperarios
      state.sectorSalud = !!preset.sectorSalud
      persist(state)
    },
    setOnboardingCompleto(state, value) {
      state.onboardingCompleto = !!value
      persist(state)
    }
  },
  actions: {
    setFlags({ commit }, flags) {
      commit('setFlags', flags)
    },
    setFlag({ commit }, payload) {
      commit('setFlag', payload)
    },
    setEdition({ commit }, edition) {
      commit('setEdition', edition)
    },
    completeOnboarding({ commit }, flags) {
      if (flags) commit('setFlags', flags)
      commit('setOnboardingCompleto', true)
    }
  }
}

// Re-export para conveniencia
export { EDITIONS }
